import React, { useState } from 'react';
import styles from './FloatingLabel.module.css';
import { getClassName } from '../../utils/className.js';

export const withFloatingLabel = (WrappedComponent) => {
  const FloatingLabelComponent = React.forwardRef((props, ref) => {
    const {
      label,
      floatingLabel = false,
      value,
      error,
      helperText,
      disabled = false,
      placeholder = '',
      isOpen = false,
      alwaysFloatLabel = false,
      onFocus,
      onBlur,
      fullWidth = false,
      className,
      style,
      labelClassName: labelClassNameProp,
      labelStyle,
      outlineClassName,
      outlineStyle,
      ...rest
    } = props;

    const [isFocused, setIsFocused] = useState(false);

    if (!floatingLabel) {
      return (
        <WrappedComponent
          {...props}
          ref={ref}
        />
      );
    }

    const hasValue = value !== undefined && value !== null && value !== '';
    const isFloating = alwaysFloatLabel || isOpen || hasValue || isFocused;
    const hasError = Boolean(error);
    const errorMessage = typeof error === 'string' && error ? error : null;
    const helperMessage = errorMessage || helperText;

    const containerClassName = getClassName(
      styles.containerFloatingLabel,
      fullWidth && styles.fullWidth,
      className
    );

    const wrapperClassName = getClassName(
      styles.floatingLabelWrapper,
      isOpen && styles.wrapperOpen,
      error && styles.wrapperError,
      disabled && styles.wrapperDisabled
    );

    const labelClassName = getClassName(
      styles.labelFloating,
      {
        [styles.labelFloatingActive]: isFloating,
        [styles.labelFloatingError]: error,
        [styles.labelFloatingDisabled]: disabled,
      },
      labelClassNameProp
    );

    const handleFocus = (e) => {
      setIsFocused(true);
      onFocus?.(e);
    };

    const handleBlur = (e) => {
      setIsFocused(false);
      onBlur?.(e);
    };

    const floatingLabelLeft = WrappedComponent.floatingLabelLeft || '10px';
    const mergedStyle = {
      '--floating-label-left': floatingLabelLeft,
      ...style
    };

    return (
      <div className={containerClassName} style={mergedStyle}>
        {label && (
          <span className={styles.labelGhost} aria-hidden="true">
            {label}
          </span>
        )}
        <div className={wrapperClassName}>
          {label && (
            <label className={labelClassName} style={labelStyle}>
              {label}
            </label>
          )}

          <WrappedComponent
            {...rest}
            ref={ref}
            value={value}
            error={error}
            disabled={disabled}
            placeholder={isFloating ? placeholder : ''}
            onFocus={handleFocus}
            onBlur={handleBlur}
            floatingLabel={true}
            isFloating={isFloating}
            fullWidth={fullWidth}
          />

          <fieldset
            className={getClassName(
              styles.notchedOutline,
              { [styles.notchedOutlinefieldsetActive]: isFloating },
              outlineClassName
            )}
            style={outlineStyle}
            aria-hidden="true"
          >
            <legend
              className={getClassName(styles.notchedOutlineLegend, {
                [styles.notchedOutlineLegendActive]: isFloating,
              })}
            >
              <span className={labelClassNameProp} style={labelStyle}>{label}</span>
            </legend>
          </fieldset>
        </div>

        {helperMessage != null && helperMessage !== '' && (
          <span
            className={getClassName(styles.helperText, hasError && styles.helperTextError)}
          >
            {helperMessage}
          </span>
        )}
      </div>
    );
  });

  FloatingLabelComponent.displayName = 'WithFloatingLabel';

  return FloatingLabelComponent;
};
